<template>
  <div class="chat-page-wrapper">
    <div class="chat-container">
      <!-- Sidebar -->
      <div class="chat-sidebar">
        <div class="sidebar-header pt-2">
          <div class="sidebar-title-row mb-2">
            <div>
              <div class="sidebar-title">AI Chat Messages</div>
              <div v-if="lastUpdated" class="last-updated">
                Last updated: {{ lastUpdated }}
              </div>
            </div>
            <div class="sidebar-actions">
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    small
                    @click="scrollToAskAI"
                    class="ask-ai-btn"
                    v-on="on"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>Ask AI</span>
              </v-tooltip>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    icon
                    small
                    @click="$router.push('/ai-cost-report')"
                    v-on="on"
                  >
                    <v-icon>mdi-currency-usd</v-icon>
                  </v-btn>
                </template>
                <span>Cost Report</span>
              </v-tooltip>
              <v-btn
                icon
                small
                @click="refreshData"
                :loading="loadingRefresh"
                class="refresh-btn"
              >
                <v-icon>mdi-refresh</v-icon>
              </v-btn>
            </div>
          </div>
          <v-text-field
            v-model="sidebarSearch"
            placeholder="Search conversations..."
            prepend-inner-icon="mdi-magnify"
            solo
            flat
            dense
            hide-details
            class="sidebar-search"
            @input="filterSidebar"
          ></v-text-field>
        </div>

        <div class="sidebar-list">
          <div
            v-for="chat in filteredChats"
            :key="chat.callerId"
            class="chat-item"
            :class="{
              active: selectedChat && selectedChat.callerId === chat.callerId,
            }"
            @click="selectChat(chat)"
          >
            <div
              class="chat-avatar"
              :style="{ backgroundColor: getUserColor(chat.callerId) }"
            >
              <span class="avatar-letter">{{
                getUserInitial(chat.name || chat.callerId)
              }}</span>
            </div>
            <div class="chat-info">
              <div class="chat-top">
                <div class="chat-name">{{ chat.name || chat.callerId }}</div>
                <div class="chat-date">{{ formatDate(chat.date) }}</div>
              </div>
              <div class="chat-preview">
                <v-icon v-if="chat.isImage" small class="mr-1"
                  >mdi-camera</v-icon
                >
                <v-icon
                  v-if="chat.status === 'ERROR'"
                  small
                  color="error"
                  class="mr-1"
                  >mdi-alert-circle</v-icon
                >
                <v-icon
                  v-if="chat.status === 'PENDING'"
                  small
                  color="warning"
                  class="mr-1"
                  >mdi-clock-outline</v-icon
                >
                <span
                  :class="{
                    'error--text': chat.status === 'ERROR',
                    'warning--text': chat.status === 'PENDING',
                    'rtl-text': isHebrewText(chat.lastMessage),
                  }"
                >
                  {{ trimText(chat.lastMessage) }}
                </span>
              </div>
            </div>
          </div>

          <div v-if="filteredChats.length === 0" class="empty-state">
            <v-icon large color="grey">mdi-chat-outline</v-icon>
            <div class="mt-2">No conversations found</div>
          </div>
        </div>
      </div>

      <!-- Chat Area -->
      <div class="chat-area">
        <div v-if="!selectedChat" class="empty-chat">
          <v-icon size="100" color="grey lighten-2">mdi-chat-outline</v-icon>
          <div class="mt-4 grey--text text--darken-1">
            Select a conversation to view messages
          </div>
        </div>

        <template v-else>
          <!-- Chat Header -->
          <div class="chat-header">
            <div class="chat-header-info">
              <div
                class="chat-avatar-large"
                :style="{
                  backgroundColor: getUserColor(selectedChat.callerId),
                }"
              >
                <span class="avatar-letter-large">{{
                  getUserInitial(selectedChat.name || selectedChat.callerId)
                }}</span>
              </div>
              <div>
                <div class="chat-header-name">
                  {{ selectedChat.name || selectedChat.callerId }}
                </div>
                <div class="chat-header-phone">
                  {{ formatPhoneNumber(selectedChat.callerId) }}
                </div>
              </div>
            </div>
          </div>

          <!-- Messages Area -->
          <div
            class="messages-container"
            ref="messagesContainer"
            @scroll="onScroll"
          >
            <div
              v-if="loadingMessages && messages.length === 0"
              class="loading-messages"
            >
              <v-progress-circular
                indeterminate
                color="green"
                size="50"
              ></v-progress-circular>
              <div class="mt-3">Loading messages...</div>
            </div>

            <div
              v-if="loadingMore && messages.length > 0"
              class="text-center py-3"
            >
              <v-progress-circular
                indeterminate
                color="primary"
                size="30"
              ></v-progress-circular>
            </div>

            <div
              v-for="message in messages"
              :key="message.Id"
              class="message-wrapper"
            >
              <!-- Question (User side - right) -->
              <div class="message-row user-message">
                <div class="message-bubble user-bubble">
                  <v-icon
                    v-if="message.Type"
                    small
                    :color="message.Type === 'WEB' ? '#2196f3' : '#4caf50'"
                    class="message-type-icon"
                  >
                    {{
                      message.Type === "WEB" ? "mdi-web" : "mdi-message-text"
                    }}
                  </v-icon>
                  <div
                    v-if="parseUrls(message.QuestionUrls).length > 0"
                    class="message-images"
                  >
                    <div
                      v-for="(url, idx) in parseUrls(message.QuestionUrls)"
                      :key="'q-' + idx"
                      class="image-container"
                    >
                      <img
                        :src="url"
                        class="message-image"
                        @click="openImageModal(url)"
                        @load="onImageLoad"
                      />
                      <v-btn
                        icon
                        small
                        class="image-download-btn"
                        @click.stop="downloadImage(url)"
                      >
                        <v-icon small>mdi-download-circle</v-icon>
                      </v-btn>
                    </div>
                  </div>
                  <audio
                    v-if="message.QuestionRecording"
                    controls
                    :src="message.QuestionRecording"
                    class="message-audio"
                  ></audio>
                  <div
                    v-if="message.Question"
                    class="message-text"
                    :class="{ 'rtl-text': isHebrewText(message.Question) }"
                  >
                    {{ trimText(message.Question) }}
                  </div>
                  <div
                    v-if="message.QuestionWithContext"
                    class="context-btn-wrapper"
                  >
                    <v-btn
                      x-small
                      text
                      color="success"
                      @click="openContextDialog(message)"
                      class="context-btn my-0"
                    >
                      <v-icon small left>mdi-information-outline</v-icon>
                      View Context
                    </v-btn>
                  </div>
                  <div class="message-time">
                    {{ formatMessageTime(message.Date) }}
                  </div>
                </div>
              </div>

              <!-- Answer (AI side - left) -->
              <div
                class="message-row ai-message"
                v-if="
                  message.Answer ||
                    message.Urls ||
                    message.Status === 'ERROR' ||
                    message.Status === 'PENDING'
                "
              >
                <div
                  class="message-bubble ai-bubble"
                  :class="{
                    'error-bubble': message.Status === 'ERROR',
                    'pending-bubble': message.Status === 'PENDING',
                  }"
                >
                  <v-icon
                    v-if="message.Type"
                    small
                    :color="message.Type === 'WEB' ? '#2196f3' : '#4caf50'"
                    class="message-type-icon"
                  >
                    {{
                      message.Type === "WEB" ? "mdi-web" : "mdi-message-text"
                    }}
                  </v-icon>
                  <div v-if="message.Status === 'ERROR'" class="error-badge">
                    <v-icon small color="white">mdi-alert-circle</v-icon>
                    <span class="ml-1">Error</span>
                  </div>
                  <div
                    v-if="message.Status === 'PENDING'"
                    class="pending-badge"
                  >
                    <v-icon small color="white">mdi-clock-outline</v-icon>
                    <span class="ml-1">Pending</span>
                  </div>
                  <div
                    v-if="parseUrls(message.Urls).length > 0"
                    class="message-images"
                  >
                    <div
                      v-for="(url, idx) in parseUrls(message.Urls)"
                      :key="'a-' + idx"
                      class="image-container"
                    >
                      <img
                        :src="url"
                        class="message-image"
                        @click="openImageModal(url)"
                        @load="onImageLoad"
                      />
                      <v-btn
                        icon
                        small
                        class="image-download-btn"
                        @click.stop="downloadImage(url)"
                      >
                        <v-icon small>mdi-download</v-icon>
                      </v-btn>
                    </div>
                  </div>
                  <div
                    v-if="message.Answer && !message.Urls"
                    class="message-text markdown-content"
                    :class="{ 'rtl-text': isHebrewText(message.Answer) }"
                    v-html="formatMarkdown(message.Answer)"
                  ></div>
                  <div
                    v-if="message.Status === 'PENDING' && !message.Answer"
                    class="message-text pending-text"
                  >
                    Waiting for response...
                  </div>
                  <div class="message-footer">
                    <div class="message-time">
                      {{ formatMessageTime(message.Date) }}
                    </div>
                    <div class="message-footer-right">
                      <div v-if="message.Provider" class="message-provider">
                        {{ message.Provider }}
                      </div>
                      <div 
                        v-if="message.Cost && message.Type !== 'WEB'" 
                        class="message-cost"
                      >
                        ${{ message.Cost.toFixed(2) }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              v-if="messages.length === 0 && !loadingMessages"
              class="empty-messages"
            >
              <v-icon large color="grey">mdi-message-outline</v-icon>
              <div class="mt-2">No messages yet</div>
            </div>
          </div>

          <!-- New Message Input Area -->
          <div v-if="isCurrentUserChat" class="message-input-wrapper">
            <div class="message-input-container">
              <div
                v-if="uploadedImages.length > 0"
                class="uploaded-images-preview"
              >
                <div
                  v-for="(img, idx) in uploadedImages"
                  :key="idx"
                  class="preview-image-wrapper"
                >
                  <img :src="img.preview" class="preview-image" />
                  <v-btn
                    icon
                    x-small
                    class="remove-image-btn"
                    @click="removeImage(idx)"
                  >
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </div>
              </div>

              <div class="input-box">
                <div class="input-row">
                  <v-btn
                    icon
                    class="attach-btn"
                    @click="pickImages"
                    :disabled="uploadedImages.length >= 4 || sendingMessage"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                  <input
                    type="file"
                    ref="imageFiles"
                    style="display: none"
                    accept="image/*"
                    multiple
                    @change="onImagesPicked"
                  />

                  <v-textarea
                    v-model="newQuestionText"
                    placeholder="Ask AI a question..."
                    solo
                    flat
                    hide-details
                    rows="1"
                    auto-grow
                    class="message-input"
                    @keydown.enter.exact.prevent="sendQuestion"
                    @paste="onPaste"
                    @drop="onDrop"
                    @dragover.prevent
                    @dragenter.prevent
                  ></v-textarea>

                  <v-select
                    v-model="selectedModel"
                    :items="aiModels"
                    item-text="label"
                    item-value="value"
                    solo
                    flat
                    hide-details
                    class="model-select"
                  >
                    <template v-slot:selection="{ item }">
                      <span class="model-select-text">{{
                        item.value.includes("Flash") ? "Fast" : "Pro"
                      }}</span>
                    </template>
                  </v-select>

                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        class="image-gen-btn"
                        :class="{ active: generateImage }"
                        @click="generateImage = !generateImage"
                        v-bind="attrs"
                        v-on="on"
                      >
                        <v-icon>mdi-image-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Generate Image</span>
                  </v-tooltip>

                  <v-btn
                    icon
                    color="success"
                    class="send-btn"
                    :loading="sendingMessage"
                    :disabled="!canSendMessage"
                    @click="sendQuestion"
                  >
                    <v-icon>mdi-send</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>

      <!-- Image Modal -->
      <v-dialog v-model="imageModal" max-width="65vw" max-height="90vh">
        <v-card class="pt-4">
          <v-card-text class="pa-2 image-modal-content">
            <img :src="selectedImage" class="modal-image" />
          </v-card-text>
          <v-card-actions class="py-3">
            <v-btn color="green" text @click="downloadImage(selectedImage)">
              <v-icon left>mdi-download</v-icon>
              Download
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" outline @click="imageModal = false">
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Context Modal -->
      <v-dialog v-model="contextModal" max-width="800px" scrollable>
        <v-card>
          <v-card-title class="py-0 headline green white--text">
            Question Context
            <v-spacer></v-spacer>
            <v-btn icon dark @click="contextModal = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-card-text class="px-4 pt-3 mt-2">
            <pre class="context-text">{{ contextContent }}</pre>
          </v-card-text>
          <v-card-actions class="mb-2 pt-0">
            <v-spacer></v-spacer>
            <v-btn color="default" text @click="contextModal = false">
              Close
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import * as markedLib from "marked";

export default {
  data() {
    return {
      chats: [],
      filteredChats: [],
      sidebarSearch: "",
      selectedChat: null,
      messages: [],
      loadingMessages: false,
      loadingMore: false,
      loadingRefresh: false,
      lastUpdated: null,

      // Pagination
      currentSkip: 0,
      pageSize: 20,
      hasMoreMessages: true,

      // Image modal
      imageModal: false,
      selectedImage: null,

      // Context modal
      contextModal: false,
      contextContent: "",

      // New message input
      newQuestionText: "",
      uploadedImages: [],
      generateImage: false,
      selectedModel: localStorage.getItem("aiModel") || "Gemini 3 Pro Preview",
      aiModels: [
        {
          label: "Gemini 3 Flash Preview (Faster)",
          value: "Gemini 3 Flash Preview",
        },
        {
          label: "Gemini 3 Pro Preview",
          value: "Gemini 3 Pro Preview",
        },
      ],
      sendingMessage: false,

      // Idle timer
      idleTimer: null,
      idleTimeout: 10 * 60 * 1000, // 10 minutes in milliseconds
    };
  },

  created() {
    this.loadSidebar();
  },

  mounted() {
    this.setupIdleTimer();
    this.$nextTick(() => {
      this.addActivityListeners();
    });
  },

  beforeDestroy() {
    this.removeActivityListeners();
    if (this.idleTimer) {
      clearTimeout(this.idleTimer);
    }
  },

  methods: {
    async loadSidebar() {
      try {
        const response = await this.$http.get(
          this.getDomain() + "/api/openai/getChatSidebar"
        );
        this.chats = response.data;
        this.filteredChats = this.chats;
        this.lastUpdated = moment().format("MMM D, h:mm A");
      } catch (error) {
        console.error("Error loading sidebar:", error);
      }
    },

    filterSidebar() {
      const search = this.sidebarSearch.toLowerCase();
      this.filteredChats = this.chats.filter(
        (chat) =>
          (chat.name && chat.name.toLowerCase().includes(search)) ||
          (chat.callerId && chat.callerId.includes(search)) ||
          (chat.lastMessage && chat.lastMessage.toLowerCase().includes(search))
      );
    },

    async selectChat(chat) {
      this.selectedChat = chat;
      this.messages = [];
      this.currentSkip = 0;
      this.hasMoreMessages = true;
      await this.loadMessages(true);
    },

    async loadMessages(scrollToBottom = false) {
      if (!this.selectedChat || this.loadingMessages) return;

      this.loadingMessages = true;

      try {
        const url =
          this.getDomain() +
          `/odata/aiQuestions?$filter=CallerId eq '${
            this.selectedChat.callerId
          }'` +
          `&$orderby=Date desc&$top=${this.pageSize}&$skip=${
            this.currentSkip
          }` +
          `&$count=true`;

        const response = await this.$http.get(url);

        const newMessages = response.data.value;

        if (newMessages.length < this.pageSize) {
          this.hasMoreMessages = false;
        }

        // Add new messages to the beginning (since we're loading older messages)
        this.messages = [...newMessages.reverse(), ...this.messages];
        this.currentSkip += newMessages.length;

        if (scrollToBottom) {
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      } catch (error) {
        console.error("Error loading messages:", error);
      } finally {
        this.loadingMessages = false;
        this.loadingMore = false;
      }
    },

    async onScroll(event) {
      const container = event.target;

      // Check if scrolled to top (with small threshold)
      if (
        container.scrollTop < 100 &&
        !this.loadingMore &&
        this.hasMoreMessages
      ) {
        const oldScrollHeight = container.scrollHeight;
        this.loadingMore = true;

        await this.loadMessages(false);

        // Maintain scroll position
        this.$nextTick(() => {
          const newScrollHeight = container.scrollHeight;
          container.scrollTop = newScrollHeight - oldScrollHeight;
        });
      }
    },

    scrollToBottom() {
      if (this.$refs.messagesContainer) {
        setTimeout(() => {
          this.$nextTick(() => {
            const container = this.$refs.messagesContainer;
            container.scrollTop = container.scrollHeight + 1000;
          });
        }, 150);
        // Additional scroll after images have had time to load
        setTimeout(() => {
          this.$nextTick(() => {
            const container = this.$refs.messagesContainer;
            container.scrollTop = container.scrollHeight + 1000;
          });
        }, 500);
      }
    },

    parseUrls(urlString) {
      if (!urlString) return [];
      // Split by comma and filter out empty/whitespace-only entries
      const urls = urlString
        .split(",")
        .map((url) => url.trim())
        .filter((url) => url.length > 0);
      console.log("Parsed URLs:", urls); // Debug log
      return urls;
    },

    trimText(text) {
      if (!text) return text;
      // Only remove leading and trailing whitespace/newlines
      return text.trimStart().trimEnd();
    },

    formatMarkdown(text) {
      if (!text) return "";

      try {
        // Use marked to render the markdown
        const html = markedLib.marked(text, {
          breaks: true,
          gfm: true,
          headerIds: false,
          mangle: false,
          smartLists: true,
          smartypants: true,
        });

        return html;
      } catch (error) {
        console.error("Error rendering markdown:", error);
        // Fallback to plain text with line breaks
        return text.replace(/\n/g, "<br>");
      }
    },

    isHebrewText(text) {
      if (!text) return false;
      // Hebrew Unicode range: \u0590-\u05FF
      const hebrewRegex = /[\u0590-\u05FF]/;
      return hebrewRegex.test(text);
    },

    getUserInitial(name) {
      if (!name) return "?";
      // Get first character, handling Hebrew or English
      const firstChar = name.trim()[0];
      return firstChar ? firstChar.toUpperCase() : "?";
    },

    getUserColor(callerId) {
      // Generate consistent color based on caller ID
      const colors = [
        "#e91e63", // pink
        "#9c27b0", // purple
        "#673ab7", // deep purple
        "#3f51b5", // indigo
        "#2196f3", // blue
        "#00bcd4", // cyan
        "#009688", // teal
        "#4caf50", // green
        "#ff9800", // orange
        "#ff5722", // deep orange
        "#795548", // brown
        "#607d8b", // blue grey
      ];

      if (!callerId) return colors[0];

      // Simple hash function
      let hash = 0;
      for (let i = 0; i < callerId.length; i++) {
        hash = callerId.charCodeAt(i) + ((hash << 5) - hash);
      }

      return colors[Math.abs(hash) % colors.length];
    },

    openImageModal(url) {
      this.selectedImage = url;
      this.imageModal = true;
    },

    openContextDialog(message) {
      this.contextContent = message.QuestionWithContext || "";
      this.contextModal = true;
    },

    downloadImage(url) {
      // For external URLs (Azure Blob Storage), open in new tab for download
      // Direct download doesn't work due to CORS restrictions
      const link = document.createElement("a");
      link.href = url;
      link.target = "_blank";
      link.download = url.split("/").pop() || "image.jpg";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    onImageLoad() {
      // When images load, scroll to bottom to ensure we're at the actual bottom
      if (this.$refs.messagesContainer) {
        this.$nextTick(() => {
          const container = this.$refs.messagesContainer;
          const isNearBottom =
            container.scrollHeight -
              container.scrollTop -
              container.clientHeight <
            200;
          // Only auto-scroll if we're already near the bottom (user intent to see latest)
          if (isNearBottom) {
            container.scrollTop = container.scrollHeight;
          }
        });
      }
    },

    formatDate(dateStr) {
      const date = moment(dateStr);
      const now = moment();

      if (date.isSame(now, "day")) {
        return date.format("h:mm A");
      } else if (date.isSame(now.clone().subtract(1, "day"), "day")) {
        return "Yesterday";
      } else if (date.isAfter(now.clone().subtract(7, "days"))) {
        return date.format("dddd");
      } else {
        return date.format("M/D/YY");
      }
    },

    formatMessageTime(dateStr) {
      const date = moment(dateStr);
      const now = moment();

      if (date.isSame(now, "day")) {
        return date.format("h:mm A");
      } else if (date.isSame(now.clone().subtract(1, "day"), "day")) {
        return "Yesterday " + date.format("h:mm A");
      } else if (date.isAfter(now.clone().subtract(7, "days"))) {
        return date.format("dddd h:mm A");
      } else {
        return date.format("MMM D, YYYY h:mm A");
      }
    },

    formatPhoneNumber(phoneNumberString) {
      if (!phoneNumberString) return "";

      if (phoneNumberString.length == 11) {
        phoneNumberString = phoneNumberString.substring(1);
      }

      var cleaned = ("" + phoneNumberString).replace(/\D/g, "");
      var match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return "(" + match[1] + ") " + match[2] + "-" + match[3];
      }
      return phoneNumberString;
    },

    async refreshData() {
      this.loadingRefresh = true;
      try {
        await this.loadSidebar();
        // Clear selected chat and messages
        this.selectedChat = null;
        this.messages = [];
        this.currentSkip = 0;
        this.hasMoreMessages = true;
      } catch (error) {
        console.error("Error refreshing data:", error);
      } finally {
        this.loadingRefresh = false;
      }
    },

    setupIdleTimer() {
      // Clear existing timer
      if (this.idleTimer) {
        clearTimeout(this.idleTimer);
      }

      // Set new timer for 10 minutes
      this.idleTimer = setTimeout(async () => {
        console.log("Idle timeout reached - refreshing data");
        await this.refreshData();
        // Set up the next timer to continue refreshing every 10 minutes while idle
        this.setupIdleTimer();
      }, this.idleTimeout);
    },

    resetIdleTimer() {
      console.log("Activity detected - resetting idle timer");
      this.setupIdleTimer();
    },

    addActivityListeners() {
      // Listen to meaningful user interactions only (removed mousemove to avoid false positives)
      const events = ["click", "keypress", "scroll", "touchstart"];
      const container = this.$el;
      if (container) {
        console.log("Setting up activity listeners on chat container");
        events.forEach((event) => {
          container.addEventListener(event, this.resetIdleTimer, true);
        });
      } else {
        console.warn("Chat container not found for activity listeners");
      }
    },

    removeActivityListeners() {
      const events = ["click", "keypress", "scroll", "touchstart"];
      events.forEach((event) => {
        const container = this.$el;
        if (container) {
          container.removeEventListener(event, this.resetIdleTimer, true);
        }
      });
    },

    scrollToAskAI() {
      // Get current user's phone number
      const currentUserPhone = this.$store.state.phoneNumber;

      // Find if there's already a chat for current user
      const userChat = this.chats.find(
        (chat) =>
          chat.phoneNumber === currentUserPhone ||
          chat.callerId === currentUserPhone
      );

      if (userChat) {
        // Select existing chat
        this.selectChat(userChat);
      } else if (this.filteredChats.length > 0) {
        // If current user has no chat, still allow viewing other chats
        this.selectChat(this.filteredChats[0]);
      }

      // Scroll to bottom and focus textarea
      this.$nextTick(() => {
        this.scrollToBottom();
        const textarea = document.querySelector(".message-input textarea");
        if (textarea) {
          textarea.focus();
        }
      });
    },

    pickImages() {
      this.$refs.imageFiles.click();
    },

    onPaste(event) {
      const items = event.clipboardData.items;
      if (!items) return;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.startsWith("image/")) {
          event.preventDefault();
          const file = item.getAsFile();
          if (file && this.uploadedImages.length < 4) {
            const reader = new FileReader();
            reader.onload = (e) => {
              this.uploadedImages.push({
                file: file,
                preview: e.target.result,
              });
            };
            reader.readAsDataURL(file);
          }
        }
      }
    },

    onImagesPicked(event) {
      const files = event.target.files;
      if (!files || files.length === 0) return;

      // Calculate how many more images we can add
      const remaining = 4 - this.uploadedImages.length;
      const filesToAdd = Math.min(files.length, remaining);

      for (let i = 0; i < filesToAdd; i++) {
        const file = files[i];
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.uploadedImages.push({
              file: file,
              preview: e.target.result,
            });
          };
          reader.readAsDataURL(file);
        }
      }

      // Clear the input
      this.$refs.imageFiles.value = "";
    },

    onDrop(event) {
      event.preventDefault();
      const files = event.dataTransfer.files;
      if (!files || files.length === 0) return;

      // Calculate how many more images we can add
      const remaining = 4 - this.uploadedImages.length;
      const filesToAdd = Math.min(files.length, remaining);

      for (let i = 0; i < filesToAdd; i++) {
        const file = files[i];
        if (file.type.startsWith("image/")) {
          const reader = new FileReader();
          reader.onload = (e) => {
            this.uploadedImages.push({
              file: file,
              preview: e.target.result,
            });
          };
          reader.readAsDataURL(file);
        }
      }
    },

    removeImage(index) {
      this.uploadedImages.splice(index, 1);
    },

    async sendQuestion() {
      if (!this.canSendMessage || !this.selectedChat) return;

      this.sendingMessage = true;

      try {
        // Create FormData
        const formData = new FormData();

        // Get phone number from selected chat
        const phoneNumber =
          this.selectedChat.phoneNumber || this.selectedChat.callerId;

        // Prepend "picture" if image generation is enabled
        const questionBody = this.generateImage
          ? `picture ${this.newQuestionText}`
          : this.newQuestionText;

        // Add form fields
        formData.append("From", phoneNumber);
        formData.append("To", "+19082018287"); // Your Twilio number
        formData.append("MessageSid", "web_" + Date.now()); // Generate a unique ID
        formData.append("Body", questionBody);
        formData.append("Type", "WEB");

        // Add model selection as a parameter
        formData.append("Model", this.selectedModel);

        // Upload images if any
        if (this.uploadedImages.length > 0) {
          formData.append("NumMedia", this.uploadedImages.length.toString());

          // Upload each image first
          for (let i = 0; i < this.uploadedImages.length; i++) {
            const imageFormData = new FormData();
            imageFormData.append("file", this.uploadedImages[i].file);

            const uploadResponse = await this.$http.post(
              "https://broadcastmessageswercber.azurewebsites.net/api/home/GetImageUrl",
              imageFormData,
              {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              }
            );

            formData.append(`MediaUrl${i}`, uploadResponse.data);
          }
        }

        // Send the question
        const apiUrl = window.location.origin.startsWith("http://localhost")
          ? "https://localhost:5001/api/openai/AskAiTwilio"
          : "https://wercbergerproductions1-kha7mhdahq-ue.a.run.app/api/openai/AskAiTwilio";

        const response = await this.$http.post(apiUrl, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        // Clear inputs
        this.newQuestionText = "";
        this.uploadedImages = [];

        // Add the returned question object to messages
        if (response.data) {
          // Normalize the response to match OData casing (PascalCase)
          const normalizedMessage = {
            Id: response.data.id,
            Question: response.data.question,
            QuestionRecording: response.data.questionRecording,
            QuestionUrls: response.data.questionUrls,
            Answer: response.data.answer,
            Status: response.data.status,
            CallUuid: response.data.callUuid,
            CallerId: response.data.callerId,
            Type: response.data.type,
            Name: response.data.name,
            Date: response.data.date,
            Urls: response.data.urls,
            Provider: response.data.provider,
            Error: response.data.error,
          };

          this.messages.push(normalizedMessage);

          // Update sidebar
          await this.loadSidebar();

          // Scroll to bottom to show the new message
          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      } catch (error) {
        console.error("Error sending question:", error);
        alert("Failed to send question. Please try again.");
      } finally {
        this.sendingMessage = false;
      }
    },

    getDomain() {
      return window.location.origin.startsWith("http://localhost")
        ? "https://localhost:5001"
        : "https://wercbergerproductions1-kha7mhdahq-ue.a.run.app";
    },
  },

  computed: {
    canSendMessage() {
      return (
        (this.newQuestionText.trim().length > 0 ||
          this.uploadedImages.length > 0) &&
        !this.sendingMessage &&
        this.selectedChat &&
        this.isCurrentUserChat
      );
    },

    isCurrentUserChat() {
      if (!this.selectedChat) return false;
      const currentUserPhone = this.$store.state.phoneNumber;
      return (
        this.selectedChat.phoneNumber === currentUserPhone ||
        this.selectedChat.callerId === currentUserPhone
      );
    },
  },

  watch: {
    selectedModel(newModel) {
      localStorage.setItem("aiModel", newModel);
    },
  },
};
</script>
<style scoped>
/* RTL support for Hebrew text */
.rtl-text.markdown-content,
.rtl-text .markdown-content {
  direction: rtl;
  text-align: right;
}

.markdown-content >>> h1,
.markdown-content >>> h2,
.markdown-content >>> h3,
.markdown-content >>> h4,
.markdown-content >>> h5,
.markdown-content >>> h6 {
  font-weight: 600 !important;
  color: #1a1a1a !important;
  line-height: 1.5 !important;
  letter-spacing: -0.01em !important;
  margin-top: 28px !important;
  margin-bottom: 14px !important;
}

.markdown-content >>> h1:first-child,
.markdown-content >>> h2:first-child,
.markdown-content >>> h3:first-child {
  margin-top: 0 !important;
}

.markdown-content >>> h1 {
  font-size: 28px !important;
}

.markdown-content >>> h2 {
  font-size: 24px !important;
}

.markdown-content >>> h3 {
  font-size: 20px !important;
}

.markdown-content >>> h4 {
  font-size: 18px !important;
}

.markdown-content >>> strong {
  font-weight: 600 !important;
  color: #1a1a1a !important;
  font-size: inherit !important;
}

.markdown-content >>> b {
  font-weight: 600 !important;
  color: #1a1a1a !important;
  font-size: inherit !important;
}

.chat-page-wrapper {
  width: 100%;
  height: 100vh;
  overflow: hidden;
  margin-bottom: -100px;
}

.chat-container {
  display: flex;
  width: 100%;
  height: 100%;
  background-color: #f0f2f5;
  overflow: hidden;
}

/* Sidebar Styles */
.chat-sidebar {
  width: 400px;
  min-width: 400px;
  background-color: #fff;
  border-right: 1px solid #e0e0e0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-header {
  background: linear-gradient(135deg, #4caf50 0%, #45a049 100%);
  padding: 20px 16px 16px;
  color: white;
}

.sidebar-title-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.sidebar-actions {
  display: flex;
  gap: 4px;
}

.ask-ai-btn {
  margin-right: 4px;
}

.sidebar-title {
  font-size: 20px;
  font-weight: 500;
}

.last-updated {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
  margin-top: 2px;
}

.refresh-btn {
  margin-left: 8px;
}

.sidebar-search {
  background-color: rgba(255, 255, 255, 0.95) !important;
  border-radius: 8px;
}

.sidebar-search >>> .v-input__control {
  min-height: 42px !important;
}

.sidebar-search >>> input {
  font-size: 14px;
}

.sidebar-list {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.chat-item {
  display: flex;
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.chat-item:hover {
  background-color: #f5f5f5;
}

.chat-item.active {
  background-color: #e8f5e9;
}

.chat-avatar {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  margin-right: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-letter {
  color: white;
  font-size: 20px;
  font-weight: 500;
  user-select: none;
}

.chat-avatar-large {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  margin-right: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-letter-large {
  color: white;
  font-size: 20px;
  font-weight: 500;
  user-select: none;
}

.chat-info {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.chat-top {
  display: flex;
  justify-content: space-between;
  margin-bottom: 4px;
}

.chat-name {
  font-weight: 500;
  font-size: 16px;
  color: #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-date {
  font-size: 12px;
  color: #667781;
  flex-shrink: 0;
  margin-left: 8px;
}

.chat-preview {
  font-size: 14px;
  color: #667781;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #667781;
}

/* Chat Area Styles */
.chat-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #efeae2;
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 10px,
    rgba(0, 0, 0, 0.03) 10px,
    rgba(0, 0, 0, 0.03) 20px
  );
  position: relative;
  min-height: 0;
  overflow: hidden;
}

.empty-chat {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  flex: 1;
  width: 100%;
}

.chat-header {
  background-color: #f0f0f0;
  padding: 12px 16px;
  border-bottom: 1px solid #d1d7db;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.chat-header-info {
  display: flex;
  align-items: center;
}

.chat-header-name {
  font-size: 16px;
  font-weight: 500;
  color: #000;
}

.chat-header-phone {
  font-size: 13px;
  color: #667781;
}

/* Messages Container */
.messages-container {
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 20px;
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 100%;
}

.message-wrapper {
  margin-bottom: 12px;
}

.message-row {
  display: flex;
  margin-bottom: 4px;
}

.user-message {
  justify-content: flex-end;
}

.ai-message {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 85%;
  padding: 12px 16px 12px 16px;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
  position: relative;
  display: flex;
  flex-direction: column;
}

.message-bubble > *:first-child {
  margin-top: 0 !important;
}

.user-bubble {
  background-color: #e1f5d5;
  border-bottom-right-radius: 2px;
}

.ai-bubble {
  background-color: #f8f8f8;
  border-bottom-left-radius: 2px;
}

.error-bubble {
  background-color: #ffebee;
  border-left: 3px solid #f44336;
}

.error-badge {
  display: inline-flex;
  align-items: center;
  background-color: #f44336;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  margin-bottom: 6px;
  font-weight: 500;
}

.pending-bubble {
  background-color: #fff8e1;
  border-left: 3px solid #ffa726;
}

.pending-badge {
  display: inline-flex;
  align-items: center;
  background-color: #ffa726;
  color: white;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  margin-bottom: 6px;
  font-weight: 500;
}

.pending-text {
  color: #999;
  font-style: italic;
}

.message-text {
  font-size: 16.5px;
  line-height: 1.8;
  color: #333;
  white-space: pre-line;
  word-wrap: break-word;
  margin: 0;
}

.markdown-content {
  white-space: normal;
  font-size: 16.5px;
  line-height: 1.8;
  color: #333;
  letter-spacing: 0.01em;
}

.markdown-content >>> p {
  margin: 0 0 18px 0;
  line-height: 1.8;
  font-size: 16.5px;
  color: #333;
}

.markdown-content >>> p:last-child {
  margin-bottom: 0;
}

.markdown-content >>> p:first-child {
  margin-top: 0;
}

.markdown-content >>> em,
.markdown-content >>> i {
  font-style: italic;
  color: #333;
}

.markdown-content >>> code {
  background-color: #f0f0f0;
  padding: 3px 6px;
  border-radius: 3px;
  font-family: "Roboto Mono", "Courier New", Consolas, monospace;
  font-size: 15px;
  color: #333;
  border: 1px solid #ddd;
  font-weight: 400;
}

.markdown-content >>> a {
  color: #1a73e8;
  text-decoration: none;
  transition: all 0.2s;
  font-weight: 400;
  font-size: inherit;
}

.markdown-content >>> a:hover {
  text-decoration: underline;
}

/* Lists */
.markdown-content >>> ul,
.markdown-content >>> ol {
  margin: 18px 0;
  padding-left: 28px;
}

.rtl-text.markdown-content >>> ul,
.rtl-text.markdown-content >>> ol {
  padding-left: 0;
  padding-right: 28px;
}

.markdown-content >>> ul {
  list-style-type: disc;
}

.markdown-content >>> ol {
  list-style-type: decimal;
}

.markdown-content >>> li {
  margin: 8px 0;
  line-height: 1.8;
  font-size: 16.5px;
  color: #333;
}

.markdown-content >>> li strong,
.markdown-content >>> li b {
  display: inline;
  margin-bottom: 0;
}

/* Blockquotes */
.markdown-content >>> blockquote {
  border-left: 3px solid #ddd;
  padding-left: 16px;
  margin: 18px 0;
  color: #555;
  font-style: italic;
  line-height: 1.8;
}

.rtl-text.markdown-content >>> blockquote {
  border-left: none;
  border-right: 4px solid #e0e0e0;
  padding-left: 0;
  padding-right: 16px;
}

/* Tables */
.markdown-content >>> table {
  border-collapse: collapse;
  margin: 16px 0;
  width: 100%;
}

.markdown-content >>> table th,
.markdown-content >>> table td {
  border: 1px solid #e0e0e0;
  padding: 8px 12px;
  text-align: left;
}

.rtl-text.markdown-content >>> table th,
.rtl-text.markdown-content >>> table td {
  text-align: right;
}

.markdown-content >>> table th {
  background-color: #f5f5f5;
  font-weight: 600;
}

/* Horizontal rule */
.markdown-content >>> hr {
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 24px 0;
}

/* Pre and code blocks */
.markdown-content >>> pre {
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 14px;
  overflow-x: auto;
  margin: 18px 0;
  line-height: 1.6;
}

.markdown-content >>> pre code {
  background-color: transparent;
  border: none;
  padding: 0;
  font-size: 14.5px;
}

/* RTL text support - applied to message bubbles */
.rtl-text {
  direction: rtl;
  text-align: right;
}

.rtl-text * {
  direction: rtl;
}

.message-images {
  margin-bottom: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.image-container {
  position: relative;
  display: inline-block;
}

.message-image {
  max-width: 280px;
  max-height: 280px;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s;
  display: block;
}

.ai-message .message-image {
  max-width: 500px;
  max-height: 500px;
}

.message-image:hover {
  transform: scale(1.02);
}

.image-download-btn {
  position: absolute;
  bottom: 8px;
  right: 8px;
  color: rgba(255, 255, 255, 0.9) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  opacity: 0;
  transition: opacity 0.2s;
}

.image-container:hover .image-download-btn {
  opacity: 1;
}

.image-modal-content {
  display: flex;
  justify-content: center;
  align-items: center;
  max-height: calc(80vh - 70px);
  overflow: auto;
}

.modal-image {
  max-width: 100%;
  max-height: calc(80vh - 70px);
  width: auto;
  height: auto;
  object-fit: contain;
}

.message-audio {
  width: 100%;
  max-width: 300px;
  height: 40px;
  margin-bottom: 4px;
}

.message-time {
  font-size: 11px;
  color: #667781;
  margin-top: 2px;
  text-align: right;
  padding-left: 24px;
}

.message-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 2px;
  gap: 8px;
  min-height: 16px;
}

.message-footer .message-time {
  padding-left: 24px;
}

.message-footer-right {
  display: flex;
  align-items: center;
  gap: 8px;
}

.message-provider {
  font-size: 10px;
  color: #667781;
  font-style: italic;
}

.message-cost {
  font-size: 10px;
  color: #4caf50;
  font-weight: 600;
  background-color: rgba(76, 175, 80, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

.message-type-icon {
  position: absolute;
  bottom: 2px;
  opacity: 1;
  font-size: 12px !important;
  z-index: 1;
}

.rtl-text .message-type-icon {
  right: 6px;
  left: auto;
}

.message-bubble:not(.rtl-text) .message-type-icon {
  left: 6px;
}

.message-input-wrapper {
  background-color: transparent;
  padding: 8px 40px 12px;
  flex-shrink: 0;
}

.message-input-container {
  max-width: 1000px;
  margin: 0 auto;
}

.input-box {
  background-color: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 28px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: box-shadow 0.2s ease;
}

.input-box:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.input-box:focus-within {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
}

.uploaded-images-preview {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
  padding: 0 8px;
}

.preview-image-wrapper {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #e0e0e0;
}

.preview-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.remove-image-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  background-color: rgba(0, 0, 0, 0.7) !important;
  color: white !important;
}

.input-row {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px 6px 4px;
}

.attach-btn {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  color: #5f6368 !important;
  margin-left: 4px;
}

.attach-btn:hover {
  background-color: #f1f3f4 !important;
}

.model-select {
  width: 70px;
  min-width: 70px;
  max-width: 70px;
  flex-shrink: 0;
  order: 3;
}

.model-select >>> .v-input__control {
  min-height: 36px !important;
}

.model-select >>> .v-input__slot {
  padding: 0 4px !important;
  box-shadow: none !important;
}

.model-select >>> .v-select__selection {
  font-size: 13px;
  color: #5f6368;
  font-weight: 500;
}

.model-select >>> .v-select__selections {
  padding: 0 !important;
}

.model-select-text {
  font-size: 13px;
  color: #5f6368;
  font-weight: 500;
}

.message-input {
  flex: 1 1 auto;
  min-width: 0;
  background-color: transparent !important;
  order: 2;
}

.message-input >>> .v-input__control {
  min-height: 36px !important;
}

.message-input >>> .v-input__slot {
  padding: 0 8px !important;
  box-shadow: none !important;
}

.message-input >>> textarea {
  max-height: 200px;
  font-size: 16px;
  line-height: 1.5;
  color: #202124;
  padding: 8px 0;
}

.message-input >>> textarea::placeholder {
  color: #5f6368;
  opacity: 1;
}

.image-gen-btn {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  order: 1;
  color: #5f6368 !important;
  transition: all 0.2s;
}

.image-gen-btn:hover {
  background-color: rgba(0, 0, 0, 0.05) !important;
}

.image-gen-btn.active {
  color: #2196f3 !important;
  background-color: rgba(33, 150, 243, 0.1) !important;
}

.send-btn {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  order: 4;
  color: #fff !important;
}

.send-btn:disabled {
  color: #dadce0 !important;
}

.send-btn:hover:not(:disabled) {
  background-color: #999 !important;
}

.empty-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #667781;
}

.loading-messages {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #667781;
  font-size: 16px;
}

/* Scrollbar Styling */
.sidebar-list::-webkit-scrollbar,
.messages-container::-webkit-scrollbar {
  width: 6px;
}

.sidebar-list::-webkit-scrollbar-track,
.messages-container::-webkit-scrollbar-track {
  background: transparent;
}

.sidebar-list::-webkit-scrollbar-thumb,
.messages-container::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.sidebar-list::-webkit-scrollbar-thumb:hover,
.messages-container::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.3);
}

/* Responsive */
@media (max-width: 960px) {
  .chat-sidebar {
    width: 320px;
  }

  .message-bubble {
    max-width: 80%;
  }
}

@media (max-width: 600px) {
  .chat-sidebar {
    width: 100%;
    position: absolute;
    z-index: 10;
    height: 100%;
  }

  .chat-sidebar.has-selection {
    display: none;
  }

  .message-bubble {
    max-width: 85%;
  }
}

/* Context Button and Dialog */
.context-btn-wrapper {
  margin-top: 6px;
  margin-bottom: 4px;
}

.context-btn {
  text-transform: none !important;
  font-size: 12px;
  height: 24px !important;
  padding: 0 8px !important;
}

.context-text {
  font-family: "Roboto Mono", "Courier New", Consolas, monospace;
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-wrap: break-word;
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #e0e0e0;
  color: #333;
  max-height: 60vh;
  overflow-y: auto;
}
</style>
